import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  User,
  Users,
  Tag,
  BarChart3,
  Bell,
  Settings as SettingsIcon,
  Warehouse,
  Navigation,
  MapPin,
  Award,
  MessageCircle,
  Calendar,
  CalendarDays,
  Sun,
  Banknote,
  UtensilsCrossed,
  ShoppingBag,
  Star,
  Gift,
  Truck,
  FileText,
} from 'lucide-react';
import { shouldShowStockNavigation } from '../../utils/commercantServices.js';
import { shouldShowInstructeurFeature, getInstructeurLabels } from '../../utils/instructeurLabels.js';

export const NAV_HELP_MAILTO = 'mailto:contact@clickorico.fr';

const SECTION_DEFS = [
  { id: 'dashboard', title: 'Tableau de bord' },
  { id: 'daily', title: 'Activité quotidienne' },
  { id: 'catalog', title: 'Catalogue' },
  { id: 'clients-marketing', title: 'Clients & Marketing' },
  { id: 'management', title: 'Gestion' },
  { id: 'configuration', title: 'Configuration' },
];

/** Ordre legacy Layout.jsx — pour tests d'équivalence. */
const LEGACY_ITEM_ORDER = [
  'overview',
  'produits',
  'prestations',
  'cartes-cadeaux',
  'instructeurs',
  'stock',
  'commandes',
  'aujourdhui',
  'planning',
  'caisse',
  'caisse-kpi',
  'clients',
  'team',
  'promotions',
  'avis',
  'fidelite',
  'livreurs',
  'zones',
  'livraison',
  'expedition',
  'click-and-collect',
  'reservations-tables',
  'analytics',
  'chat',
  'communication',
  'documents',
  'parametres',
];

const SECTION_ITEM_ORDER = {
  dashboard: ['overview', 'analytics', 'commandes'],
  daily: [
    'aujourdhui',
    'planning',
    'caisse',
    'caisse-kpi',
    'click-and-collect',
    'livraison',
    'livreurs',
    'zones',
    'expedition',
    'reservations-tables',
  ],
  catalog: ['produits', 'prestations', 'cartes-cadeaux', 'instructeurs', 'stock', 'promotions'],
  'clients-marketing': ['clients', 'fidelite', 'avis', 'communication', 'chat'],
  management: ['team', 'documents'],
  configuration: ['parametres'],
};

function buildModuleFlags(commercantData, commercantDataLoaded, navOptions = {}) {
  const defaultTrue = !commercantDataLoaded;
  const moduleProduitsEnabled = commercantData?.moduleProduits ?? defaultTrue;
  const modulePrestationsEnabled = commercantData?.modulePrestations ?? defaultTrue;
  // Lot 4B tip — Cartes cadeaux = core nav (indépendant de moduleCartesCadeaux DB)
  const moduleCartesCadeauxEnabled = true;
  let moduleCaisseEnabled = commercantData?.moduleCaisse ?? defaultTrue;
  if (navOptions.preprodSuperAdminPosRecette && navOptions.commercantId) {
    moduleCaisseEnabled = true;
  }
  return {
    livraisonEnabled: commercantData?.livraison ?? defaultTrue,
    clickAndCollectEnabled: commercantData?.clickAndCollect ?? defaultTrue,
    modulePrestationsEnabled,
    moduleProduitsEnabled,
    moduleCartesCadeauxEnabled,
    moduleCaisseEnabled,
    moduleExpeditionEnabled: commercantData?.moduleExpedition ?? false,
    reservationTableEnabled: commercantData?.reservationTable ?? false,
    instructeursEnabled: shouldShowInstructeurFeature(commercantData?.categorie),
    instructeurNavLabel: getInstructeurLabels(commercantData?.categorie).label,
    stockEnabled: commercantDataLoaded
      ? shouldShowStockNavigation(commercantData)
      : moduleProduitsEnabled || modulePrestationsEnabled,
  };
}

function buildItemDefinitions(commercantId, flags) {
  const cid = commercantId;
  const {
    livraisonEnabled,
    clickAndCollectEnabled,
    modulePrestationsEnabled,
    moduleProduitsEnabled,
    moduleCartesCadeauxEnabled,
    moduleCaisseEnabled,
    moduleExpeditionEnabled,
    reservationTableEnabled,
    instructeursEnabled,
    instructeurNavLabel,
    stockEnabled,
  } = flags;

  const defs = {
    overview: commercantId
      ? {
          id: 'overview',
          name: "Vue d'ensemble",
          href: `/commercants/${cid}`,
          icon: LayoutDashboard,
          tab: 'overview',
          permission: null,
        }
      : null,
    produits: moduleProduitsEnabled
      ? {
          id: 'produits',
          name: 'Mes Produits',
          href: cid ? `/commercants/${cid}?tab=produits` : '/produits',
          icon: Package,
          tab: 'produits',
          permission: 'GESTION_PRODUITS',
        }
      : null,
    prestations: modulePrestationsEnabled
      ? {
          id: 'prestations',
          name: 'Mes Prestations',
          href: cid ? `/commercants/${cid}?tab=prestations` : '#',
          icon: Calendar,
          tab: 'prestations',
          permission: null,
        }
      : null,
    'cartes-cadeaux': moduleCartesCadeauxEnabled
      ? {
          id: 'cartes-cadeaux',
          name: 'Cartes cadeaux',
          href: cid ? `/commercants/${cid}?tab=cartes-cadeaux` : '#',
          icon: Gift,
          tab: 'cartes-cadeaux',
          permission: null,
        }
      : null,
    instructeurs: null,
    stock: stockEnabled
      ? {
          id: 'stock',
          name: 'Ingrédients & ruptures',
          href: cid ? `/commercants/${cid}?tab=stock` : '/ingredients',
          icon: Warehouse,
          tab: 'stock',
          permission: 'GESTION_STOCK',
        }
      : null,
    commandes: {
      id: 'commandes',
      name: 'Mes Commandes',
      href: cid ? `/commercants/${cid}?tab=commandes` : '/commandes',
      icon: ShoppingCart,
      tab: 'commandes',
      permission: 'GESTION_COMMANDES',
      variant: 'commandes',
    },
    aujourdhui: modulePrestationsEnabled
      ? {
          id: 'aujourdhui',
          name: "Aujourd'hui",
          href: '/aujourdhui',
          icon: Sun,
          tab: null,
          permission: 'GESTION_COMMANDES',
        }
      : null,
    planning: modulePrestationsEnabled
      ? {
          id: 'planning',
          name: 'Planning',
          href: '/planning',
          icon: CalendarDays,
          tab: null,
          permission: 'GESTION_COMMANDES',
        }
      : null,
    caisse: moduleCaisseEnabled
      ? {
          id: 'caisse',
          name: 'Caisse (POS)',
          href: cid ? `/caisse?commercantId=${cid}` : '/caisse',
          icon: Banknote,
          tab: null,
          permission: 'GESTION_CAISSE',
        }
      : null,
    'caisse-kpi': moduleCaisseEnabled
      ? {
          id: 'caisse-kpi',
          name: 'KPI Caisse',
          href: cid ? `/caisse/kpi?commercantId=${cid}` : '/caisse/kpi',
          icon: BarChart3,
          tab: null,
          permission: 'GESTION_CAISSE',
        }
      : null,
    documents: {
      id: 'documents',
      name: 'Documents',
      href: '/documents',
      icon: FileText,
      tab: null,
      permission: null,
    },
    clients: {
      id: 'clients',
      name: 'Mes Clients',
      href: '/clients',
      icon: User,
      tab: null,
      permission: null,
    },
    team: null, // résolu via roleInterne
    promotions: {
      id: 'promotions',
      name: 'Mes Promotions',
      href: cid ? `/commercants/${cid}?tab=promotions` : '/promotions',
      icon: Tag,
      tab: 'promotions',
      permission: 'GESTION_PROMOTIONS',
      navBadge: 'nouveau',
    },
    avis: {
      id: 'avis',
      name: 'Avis',
      href: cid ? `/commercants/${cid}?tab=avis` : '#',
      icon: Star,
      tab: 'avis',
      permission: 'GESTION_AVIS',
    },
    fidelite: {
      id: 'fidelite',
      name: 'Fidélité',
      href: cid ? `/commercants/${cid}?tab=fidelite` : '#',
      icon: Award,
      tab: 'fidelite',
      permission: 'GESTION_FIDELITE',
    },
    livreurs: livraisonEnabled
      ? {
          id: 'livreurs',
          name: 'Livreurs',
          href: cid ? `/commercants/${cid}?tab=livreurs` : '#',
          icon: Navigation,
          tab: 'livreurs',
          permission: 'GESTION_LIVRAISON',
        }
      : null,
    livraison: livraisonEnabled
      ? {
          id: 'livraison',
          name: 'Livraison',
          href: cid ? `/commercants/${cid}?tab=livraison` : '#',
          icon: Truck,
          tab: 'livraison',
          permission: 'GESTION_LIVRAISON',
        }
      : null,
    zones: livraisonEnabled
      ? {
          id: 'zones',
          name: 'Zones de livraison',
          href: cid ? `/commercants/${cid}?tab=zones` : '#',
          icon: MapPin,
          tab: 'zones',
          permission: 'GESTION_LIVRAISON',
        }
      : null,
    expedition: moduleExpeditionEnabled
      ? {
          id: 'expedition',
          name: 'Expédition',
          href: cid ? `/commercants/${cid}?tab=expedition` : '#',
          icon: Package,
          tab: 'expedition',
          permission: null,
        }
      : null,
    'click-and-collect': clickAndCollectEnabled
      ? {
          id: 'click-and-collect',
          name: 'Click & Collect',
          href: cid ? `/commercants/${cid}?tab=clickandcollect` : '#',
          icon: ShoppingBag,
          tab: 'clickandcollect',
          permission: null,
        }
      : null,
    'reservations-tables': reservationTableEnabled
      ? {
          id: 'reservations-tables',
          name: 'Réservations tables',
          href: cid ? `/commercants/${cid}?tab=reservations-tables` : '#',
          icon: UtensilsCrossed,
          tab: 'reservations-tables',
          permission: null,
          variant: 'reservations-tables',
        }
      : null,
    analytics: {
      id: 'analytics',
      name: 'Analytics',
      href: cid ? `/commercants/${cid}?tab=analytics` : '/analytics',
      icon: BarChart3,
      tab: 'analytics',
      permission: 'GESTION_ANALYTICS',
    },
    chat: {
      id: 'chat',
      name: 'Chat',
      href: cid ? `/commercants/${cid}?tab=chat` : '/chat',
      icon: MessageCircle,
      tab: 'chat',
      permission: 'GESTION_CHAT',
    },
    communication: {
      id: 'communication',
      name: 'Communication',
      href: cid ? `/commercants/${cid}?tab=communication` : '/notifications',
      icon: Bell,
      tab: 'communication',
      permission: 'GESTION_NOTIFICATIONS',
      navBadge: 'nouveau',
    },
    parametres: {
      id: 'parametres',
      name: 'Paramètres',
      href: cid ? `/commercants/${cid}?tab=parametres` : '/parametres-commercant',
      icon: SettingsIcon,
      tab: 'parametres',
      permission: null,
    },
  };

  return defs;
}

function resolveTeamItem(storedUser, commercantId) {
  const isGerantOrNoRole = !storedUser?.roleInterne || storedUser?.roleInterne === 'GERANT';
  if (!isGerantOrNoRole) return null;
  return {
    id: 'team',
    name: 'Mon Équipe',
    href: '/equipe/presences',
    icon: Users,
    tab: null,
    permission: null,
  };
}

function pickItems(itemIds, defs, storedUser, commercantId) {
  return itemIds
    .map((id) => {
      if (id === 'team') return resolveTeamItem(storedUser, commercantId);
      return defs[id] ?? null;
    })
    .filter(Boolean);
}

/**
 * Liste plate legacy — même visibilité que Layout.jsx avant refactor.
 */
export function buildLegacyCommercantNavFlat(ctx) {
  const { commercantId, commercantData, commercantDataLoaded, storedUser, navOptions } = ctx;
  const flags = buildModuleFlags(commercantData, commercantDataLoaded, {
    ...navOptions,
    commercantId,
  });
  const defs = buildItemDefinitions(commercantId, flags);
  return pickItems(LEGACY_ITEM_ORDER, defs, storedUser, commercantId);
}

/**
 * Sections commerçant avec items visibles — masque sections vides.
 */
export function buildCommercantNavSections(ctx) {
  const { commercantId, commercantData, commercantDataLoaded, storedUser, navOptions } = ctx;
  const flags = buildModuleFlags(commercantData, commercantDataLoaded, {
    ...navOptions,
    commercantId,
  });
  const defs = buildItemDefinitions(commercantId, flags);

  return SECTION_DEFS.map((section) => ({
    ...section,
    items: pickItems(SECTION_ITEM_ORDER[section.id] || [], defs, storedUser, commercantId),
  })).filter((section) => section.items.length > 0);
}

/** IDs visibles — pour tests d'équivalence legacy vs sections. */
export function visibleCommercantNavIds(ctx) {
  return buildLegacyCommercantNavFlat(ctx).map((item) => item.id).sort();
}

export function visibleCommercantNavIdsFromSections(ctx) {
  return buildCommercantNavSections(ctx)
    .flatMap((s) => s.items.map((i) => i.id))
    .sort();
}
